import React, { ReactElement, useState } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames/bind';
import ChangeChart from '../../../components/widgets/Dashboard/ChangeChart';
import Monitor from '../../../components/widgets/Dashboard/Monitor';
import CreditRiskLevel from '../../../components/widgets/Dashboard/CreditRiskLevel';
import RecentChange from '../../../components/widgets/Dashboard/RecentChange';
import RiskDistribution from '../../../components/widgets/Dashboard/RiskDistribution';
import WatchListDistribution from '../../../components/widgets/Dashboard/WatchListDistribution';
import RecentRecChangesWidget from '../../../components/widgets/RecentRecChanges/RecentRecChangesWidget';
import useFetchWidgetMetrics from '../../../hooks/riskProducts/dashboard/useFetchWidgetMetrics';
import { useFetchWatchLists } from '../../../hooks/watchLists';
import { RpColumn } from '../../../models/RpColumn';
import styles from './Dashboard.module.scss';
import Research from '../../../components/widgets/Dashboard/Research';
import {
  OpenArticlePreviewOnClickKey,
  getArticlePreviewHandler,
} from '../../../components/ArticlePreview/ArticlePreview.logic';
import { useBreakpoint, useLocalStorage } from '../../../hooks/common';
import { useCheckArticleAccessStatus } from '../../../hooks/articles';
import ArticlePreview from '../../../components/ArticlePreview';
import { shouldNavigate } from '../../../utils/navigationUtils';

const cx = classNames.bind(styles);

export interface DashboardOutletContext {
  isRpColumnsLoading: boolean;
  rpColumns: RpColumn[];
}

const Dashboard = (): ReactElement | null => {
  const { watchListId = '' } = useParams<string>();
  const { isRpColumnsLoading, rpColumns } = useOutletContext<DashboardOutletContext>();
  const { isLoading: isWatchListsLoading } = useFetchWatchLists();
  const { isLoading: isWidgetMetricsLoading } = useFetchWidgetMetrics();
  const [previewArticleId, setPreviewArticleId] = useState<number | null>(null);
  const { isDesktop } = useBreakpoint();
  const navigate = useNavigate();

  const checkArticleAccessStatus = useCheckArticleAccessStatus();

  const [openArticlePreviewOnClick, setOpenArticlePreviewOnClick] = useLocalStorage<boolean>(
    OpenArticlePreviewOnClickKey,
    true
  );
  const showArticlePreview = !!(isDesktop && previewArticleId);
  if (isWatchListsLoading || isRpColumnsLoading || isWidgetMetricsLoading) {
    return null;
  }

  const onPreviewableArticleClick = getArticlePreviewHandler(
    setPreviewArticleId,
    isDesktop,
    navigate,
    checkArticleAccessStatus,
    openArticlePreviewOnClick
  );

  return (
    <div className={cx('body')}>
      <div className={cx('row')}>
        <div className={cx('column')}>
          <Monitor watchListId={watchListId} rpColumns={rpColumns} />
        </div>
      </div>
      <div className={cx('row')}>
        <div className={cx('column', 'column-1-3')}>
          <CreditRiskLevel watchListId={watchListId} />
        </div>
        <div className={cx('column', 'column-2-3')}>
          <WatchListDistribution watchListId={watchListId} />
        </div>
      </div>
      <div className={cx('row')}>
        <div className={cx('column', 'column-1-3')}>
          <ChangeChart watchListId={watchListId} />
        </div>
        <div className={cx('column', 'column-2-3')}>
          <RiskDistribution watchListId={watchListId} />
        </div>
      </div>
      <div className={cx('row')}>
        <div className={cx('column', 'column-1-3')}>
          <RecentChange watchListId={watchListId} />
        </div>
        <div className={cx('column', 'column-1-3')}>
          <Research
            watchListId={watchListId}
            openArticlePreviewOnClick={openArticlePreviewOnClick}
            setOpenArticlePreviewOnClick={setOpenArticlePreviewOnClick}
            onPreviewableArticleClick={onPreviewableArticleClick}
            isPreviewable={!shouldNavigate(isDesktop, openArticlePreviewOnClick)}
          />
        </div>
        <div className={cx('column', 'column-1-3')}>
          <RecentRecChangesWidget watchlistId={watchListId} filters={{}} />
        </div>
      </div>
      {previewArticleId && (
        <ArticlePreview articleId={previewArticleId} setArticleId={setPreviewArticleId} show={showArticlePreview} />
      )}
    </div>
  );
};

export default Dashboard;
